import { useState, useRef, useEffect } from 'react';
import * as api from '../api/storageApi';
import type { DriveFile, DriveFolder } from '../types';

interface Props {
  file: DriveFile | DriveFolder;
  onClose: () => void;
  onDone: () => void;
}

export default function RenameModal({ file, onClose, onDone }: Props) {
  // Para carpetas, no hay extensión
  const isFolder = !('size' in file);
  const ext      = !isFolder && file.name.includes('.') ? '.' + file.name.split('.').pop() : '';
  const baseName = ext ? file.name.slice(0, -ext.length) : file.name;

  const [name, setName]     = useState(baseName);
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = (name.trim() + ext);

    if (trimmed === file.name) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      // Normalizar path original (quitar / iniciales)
      const normalizedSource = file.path.replace(/^\/+/, '');
      
      // Determinar directorio padre (mantener estructura de directorios)
      // Para carpetas: file.path termina con "/" entonces extraer todo menos el último segmento
      // Para archivos: file.path no termina con "/" entonces extraer todo menos el archivo
      const pathSegments = normalizedSource.replace(/\/$/, '').split('/');
      const dir = pathSegments.length > 1 ? pathSegments.slice(0, -1).join('/') : '';
      
      // Construir ruta destino con el nuevo nombre en el mismo directorio
      const dest = dir ? `${dir}/${trimmed}` : trimmed;
      
      // Determinar tipo: carpeta o archivo
      const itemType: 'file' | 'folder' = isFolder ? 'folder' : 'file';
      
      // Para carpetas, las rutas deben terminar en /
      // Para archivos, las rutas NO deben terminar en /
      const finalSource = isFolder ? `${normalizedSource.replace(/\/$/, '')}/` : normalizedSource.replace(/\/$/, '');
      const finalDest = isFolder ? `${dest}/` : dest;
      
      const result = await api.moveFiles([{ type: itemType, path: finalSource, destination: finalDest }]);
      
      // Si es un job asíncrono, hacer polling
      if (result.jobId) {
        const job = await api.pollJob(result.jobId, () => {
          // Progreso del rename (no mostramos UI de progreso en este modal)
        });
        
        if (job.status === 'completed' || job.status === 'completed_with_errors') {
          onDone();
        } else if (job.status === 'failed') {
          const errorMsg = job.errorDetails && job.errorDetails.length > 0
            ? job.errorDetails.map(e => `${e.source}: ${e.error}`).join('\n')
            : 'La operación de renombrar falló';
          setError(errorMsg);
        }
      } else {
        // Síncrono - completado inmediatamente
        onDone();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al renombrar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block feebbo-modal" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,20,0.7)', backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-pencil me-2" />
              Renombrar {isFolder ? 'carpeta' : 'archivo'}
            </h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <label className="form-label fw-medium" style={{ color: 'var(--feebbo-muted)' }}>Nuevo nombre</label>
              <div className="input-group">
                <input
                  ref={inputRef}
                  className={`form-control feebbo-input ${error ? 'is-invalid' : ''}`}
                  value={name}
                  onChange={e => { setName(e.target.value); setError(''); }}
                  disabled={saving}
                />
                {ext && <span className="input-group-text text-body-secondary">{ext}</span>}
                {error && <div className="invalid-feedback" style={{ whiteSpace: 'pre-line' }}>{error}</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-feebbo-outline" onClick={onClose} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-feebbo" disabled={saving || !name.trim()}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2" />Guardando…</> : 'Renombrar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
